import { v4 as uuidv4 } from 'uuid';
import { TAddDocumentResponse, TIdentificationStore } from '../types';
import { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  DeletionPermissionEnum,
  DocStatusEnum,
  TDocument,
  TDocumentsInStore,
} from '~/modules/identification/forms/Documents/types.ts';
import { TErrorResponse, TSignatureValidateDocumentResponse } from '~/store/types.ts';
import { EMPTY_ERROR } from '~/constants/errorCodes';
import { DOCUMENT_WEB_BLOCK } from '~/modules/identification/forms/Documents/constants.tsx';

export const deleteDocumentFulfilledAction = (
  state: TIdentificationStore,
  action: PayloadAction<{ idDoc: number; blockId: number }>
) => {
  const idDoc = action.payload.idDoc;
  const blockId = action.payload.blockId;
  const documents = { ...(state.formData.documents as TDocumentsInStore).documents };
  const documentBlock = getDocsByBlockId(blockId) as keyof typeof documents;

  (state.formData.documents as TDocumentsInStore).documents[documentBlock] = documents[
    documentBlock
  ]?.filter((doc) => idDoc !== doc.idDoc);
};

export const addDocumentFulfilledAction = (
  state: TIdentificationStore,
  action: PayloadAction<TAddDocumentResponse>
) => {
  const webId = action.payload.webId;
  const documentId = action.payload.docInfo.documentId;
  const signatureId = action.payload.docInfo.signatureId;
  const blockId = action.payload.blockId;
  const documents = { ...(state.formData.documents as TDocumentsInStore).documents };
  const documentBlock = getDocsByBlockId(blockId) as keyof typeof documents;

  (state.formData.documents as TDocumentsInStore).documents[documentBlock] = [
    ...documents[documentBlock],
    {
      id: uuidv4(),
      idDoc: documentId,
      nameDoc: '',
      idDocSign: signatureId,
      docStatus: [DocStatusEnum.NOT_VERIFIED],
      deletionPermission: DeletionPermissionEnum.ALLOWED,
      idDictWebDocument: webId,
    },
  ];
};

export const validateDocumentSignAction = (
  state: TIdentificationStore,
  action: PayloadAction<TSignatureValidateDocumentResponse>
) => {
  const documentKepVerificationStatus = action.payload.documentKepVerificationStatus || {};
  const generatedProtocols = action.payload.generatedProtocols || [];
  const hasKepVerificationStatus = Object.keys(documentKepVerificationStatus).length;

  if (!generatedProtocols.length && !hasKepVerificationStatus) {
    state.loading = false;
    state.error.errorMessage = errorMessages.SERVICE_UNAVAILABLE;
    return;
  }

  const documents = { ...(state.formData.documents as TDocumentsInStore).documents };

  let incompleteDocuments: TDocument[] = [];
  for (const key in documents) {
    documents[key as keyof typeof documents].forEach((doc, index) => {
      let newDocStatus: DocStatusEnum[] = [DocStatusEnum.NOT_VERIFIED];
      if (doc.idDoc !== null && generatedProtocols.includes(doc.idDoc)) {
        newDocStatus = [DocStatusEnum.SUCCESS];
      } else if (
        doc.idDoc !== null &&
        Object.prototype.hasOwnProperty.call(documentKepVerificationStatus, doc.idDoc)
      ) {
        const reasons = documentKepVerificationStatus[doc.idDoc];
        newDocStatus = reasons?.length ? reasons : [DocStatusEnum.INVALID_SIGNATURE];
        incompleteDocuments.push({ ...doc, docStatus: newDocStatus });
      }

      documents[key as keyof typeof documents][index] = {
        ...doc,
        docStatus: newDocStatus,
      };
    });
  }

  state.loading = false;
  state.error.errorMessage = incompleteDocuments.length
    ? errorMessages.SIGNATURE_VERIFICATION_FAILED
    : null;
  (state.formData.documents as TDocumentsInStore).documents = documents;
};

const getDocsByBlockId = (blockId: number) => {
  switch (blockId) {
    case 1:
      return 'mainDocuments';
    case 2:
      return 'additionalMainDocuments';
    case 3:
      return 'additionalDocuments';
    case 4:
      return 'assetManagersDocuments';
    default:
      return '';
  }
};

export const getDocumentWebBlockByBlockId = (blockId: number): DOCUMENT_WEB_BLOCK | undefined => {
  switch (blockId) {
    case 1:
      return DOCUMENT_WEB_BLOCK.MAIN;
    case 2:
      return DOCUMENT_WEB_BLOCK.ADDITIONAL_MAIN;
    case 3:
      return DOCUMENT_WEB_BLOCK.ADDITIONAL;
    default:
      return undefined;
  }
};

export const defaultFulfilledAction = (state: TIdentificationStore) => {
  state.loading = false;
  state.error = EMPTY_ERROR;
};

export const documentsError = (
  state: TIdentificationStore,
  action: { error: SerializedError; payload?: { data: TErrorResponse } | unknown }
) => {
  state.loading = false;
  state.error.errorMessage =
    (action.payload as { data: TErrorResponse })?.data?.message ||
    errorMessages.SERVICE_UNAVAILABLE;
};
